import { ITooltipProps } from "@edgetier/components";
import { TooltipProps } from "../tooltip";
import type { CSSProperties } from "react";
import type { MenuProps } from "../menu";

export const MINIMUM_MULTIPLE_WIDTH = 600;
export const MINIMUM_SINGLE_WIDTH = 300;

export const MAXIMUM_TOOLTIP_ITEMS = 30;

export const MINIMUM_TAGS_TO_OPEN_ACCORDIONS = 11;

export const APPLY_BUTTON_TEXT = "Done";
export const CANCEL_BUTTON_TEXT = "Cancel";

export const LIMIT_REACHED_MESSAGE = "Item limit reached";

export const PLACEMENT_MAP: Record<Exclude<ITooltipProps["placement"], undefined>, TooltipProps["position"]> = {
    "bottom-start": "bottom-start",
    "bottom-end": "bottom-end",
    "bottom-center": "bottom",
    "top-start": "top-start",
    "top-center": "top",
    "top-end": "top-end",
    "left-end": "left-end",
    "left-center": "left",
    "left-start": "left-start",
    "right-end": "right-end",
    "right-center": "right",
    "right-start": "right-start",
    center: "top",
};

/**
 * The `Menu` props an `inline` select renders with. Nothing floats, fades or measures the viewport: the outer popover
 * the select sits in already does, and a nested `size` middleware would publish its own `--overlay-available-height`
 * on the inline dropdown, shadowing the outer popover's.
 */
export const INLINE_MENU_PROPS: Pick<MenuProps, "withinPortal" | "transitionProps" | "middlewares"> = {
    withinPortal: false,
    transitionProps: { duration: 0 },
    middlewares: { flip: false, shift: false, size: false },
};

/**
 * Mantine writes a dropdown's floating `top`/`left` and its width as inline styles. An `inline` select's dropdown sits
 * in the normal flow instead: the static position ignores `top`/`left`, and it takes its parent's width. The global
 * `Menu` falls back to a 220px width when none is given; only this `width: auto` removes it.
 */
export const INLINE_DROPDOWN_STYLE: CSSProperties = { position: "static", width: "auto" };

// The selected list sits to the right of the options, so this is the arrow that walks into it.
export const ENTER_SELECTED_LIST_KEY = "ArrowRight";

// Finds the rows of the selected list, which carry `data-selected-item`. The select marks them itself rather than
// relying on the menu's own `data-menu-item`, so that this does not depend on Mantine's internals.
export const SELECTED_ITEM_SELECTOR = "[data-selected-item]";

// Empties the search box of the last thing chosen, the usual multiple select gesture.
export const REMOVE_LAST_SELECTED_KEY = "Backspace";

// Opens a closed select, rather than letting the key reach the form around it.
export const OPEN_KEY = "Enter";

// Remove the selected item the keyboard is on.
export const REMOVE_SELECTED_KEYS = ["Delete", "Backspace"];

// Finds the menu's own buttons, which carry `data-select-action`. The menu is drawn in a portal, so the tab order
// steps past it to the next field on the page and the keyboard has to be handed to them deliberately.
export const SELECT_ACTION_SELECTOR = "[data-select-action]";

// Hands the keyboard between the search box and those buttons.
export const MOVE_TO_ACTIONS_KEY = "Tab";
